/**
 * BHL manufacturer part number (MPN) lookup (P210, Nathan Keeble 2026-10-26; Neil's amendment 1).
 * Rendered by snippets/bhl-mpn-lookup.liquid on the search page and in predictive search.
 *
 * Every product carries its MPN as a tag "mpn:<as supplied>" (plus a normalised "mpn:<lower case, no separators>"
 * where they differ), Gate B decision B1. Shopify's search does not match a bare query against a prefixed tag, so a
 * product whose MPN is only in its tags is not found (observed 2026-10-26: "005060W" returns unrelated products).
 * The quoted tag syntax is exact and case-insensitive on this store: tag:"mpn:005060W" returns exactly W-436249 and
 * tag:"mpn:v4043h1056u" exactly the two V4043H1056/U valves (observed 2026-10-26 on /search/suggest.json). One request
 * asks for the typed form and the normalised form; a product is kept only when one of its mpn: tags, normalised,
 * equals the normalised query, so a loose match can never show.
 *
 * Matches the page or dropdown already shows are not repeated. Nothing is opened automatically; nothing is drawn when
 * nothing is found. EAN search needs nothing here (barcodes are searched by Shopify).
 *
 * Order with the supplier code lookup (gate round 1, condition 1): the element's `ready` promise resolves once with
 * the product paths it lists; on a page with no results the supplier lookup opens a product only after that, and only
 * when this lookup lists nothing else (gateOpen in assets/bhl-supplier-code.js). Neither lookup treats the other's
 * panel as "already shown", so each panel's content does not depend on which answer arrives first. In the dropdown,
 * an answer for text the shopper has typed past is not drawn.
 *
 * The decisions are pure functions (mpnLookupFor, pickMpn, withoutShown) so they run under node in the tests.
 */

export const MIN_LENGTH = 3;
export const MAX_LENGTH = 40;

/** Identifier normal form (as snippets/bhl-normalise.liquid and the supplier code lookup). */
export function normalise(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-./]/g, '');
}

/**
 * The search for a query, or null when it cannot be a part number: 3 to 40 characters of letters, digits and
 * - . / _ + (starting with a letter or digit), at least one digit.
 * @param {string} raw
 * @returns {{code: string, norm: string, query: string} | null}
 */
export function mpnLookupFor(raw) {
  const code = String(raw ?? '').trim();
  if (code.length < MIN_LENGTH || code.length > MAX_LENGTH) return null;
  if (!/^[A-Za-z0-9][A-Za-z0-9\-./_+]*$/.test(code) || !/[0-9]/.test(code)) return null;
  const norm = normalise(code);
  if (norm.length < MIN_LENGTH) return null;
  const values = [code];
  if (norm !== code.toLowerCase()) values.push(norm);
  return { code, norm, query: values.map((v) => `tag:"mpn:${v}"`).join(' OR ') };
}

/** Path of a product URL from the JSON, without the search tracking parameters. */
export function productPath(url, origin = 'https://x.invalid') {
  try {
    return new URL(url, origin).pathname;
  } catch (_) {
    return '';
  }
}

/**
 * The products whose mpn: tag equals the query once normalised, in the search order, each with the tag as supplied.
 * @param {Array<any>} products
 * @param {string} norm
 */
export function pickMpn(products, norm) {
  const out = [];
  const seen = new Set();
  for (const product of products || []) {
    if (!product || !product.url || seen.has(product.url)) continue;
    let mpn = null;
    for (const tag of product.tags || []) {
      const t = String(tag);
      if (t.toLowerCase().startsWith('mpn:') && normalise(t.slice(4)) === norm) {
        // prefer the tag as supplied (with its capitals and separators) over the normalised twin
        if (mpn === null || /[A-Z\-./]/.test(t.slice(4))) mpn = t.slice(4);
      }
    }
    if (mpn !== null) {
      seen.add(product.url);
      out.push({ product, mpn });
    }
  }
  return out;
}

/**
 * Gate round 2, condition 1: one request to the predictive search JSON. Returns the products, or the string 'failed'
 * for a network error, an HTTP error or a body that is not the expected JSON, so a failure is never mistaken for a
 * confirmed "no match" (the supplier code lookup does not open a product on 'failed'). An abort is rethrown.
 */
export async function fetchMpn(fetchImpl, url, signal) {
  let response;
  try {
    response = await fetchImpl(url, { signal, headers: { Accept: 'application/json' } });
  } catch (e) {
    if (signal?.aborted) throw e;
    return 'failed';
  }
  if (!response || !response.ok) return 'failed';
  try {
    const json = await response.json();
    const products = json?.resources?.results?.products;
    return Array.isArray(products) ? products : 'failed';
  } catch (_) {
    return 'failed';
  }
}

/**
 * Gate round 2, condition 1: a product is shown once. The supplier code panel takes precedence; this panel leaves out
 * every product whose id (or path) the supplier panel already lists. Re-run whenever the supplier panel is redrawn
 * (event bhl:supplier-drawn), so the end state does not depend on which answer arrived first.
 * @param {Array<{product: any}>} items
 * @param {Set<string>} keys - product ids and paths listed elsewhere
 */
export function dedupe(items, keys) {
  return items.filter((m) => !keys.has(String(m.product.id)) && !keys.has(productPath(m.product.url)));
}

/**
 * Gate round 3: the search surface an element belongs to, so the one-place rule never reaches another surface on the
 * same page (the predictive dropdown in the header and the search page results are separate): the predictive search
 * component for the dropdown, the section for the search page.
 */
export function surfaceOf(el) {
  if (el?.dataset?.mode === 'predictive') {
    return el.closest('predictive-search-component') || el.closest('[data-search-results]') || el.parentElement || null;
  }
  return el?.closest('.shopify-section') || el?.closest('results-list') || el?.parentElement || null;
}

/** Product ids and paths listed by the supplier code panels inside `root` only (none without a root). */
export function supplierKeysIn(root, origin = 'https://x.invalid') {
  const keys = new Set();
  if (!root || typeof root.querySelectorAll !== 'function') return keys;
  root.querySelectorAll('bhl-supplier-code [data-product-id]').forEach((el) => keys.add(String(el.getAttribute('data-product-id'))));
  root.querySelectorAll('bhl-supplier-code a[href*="/products/"]').forEach((a) => keys.add(productPath(a.getAttribute('href') || '', origin)));
  return keys;
}

/** The matches without the products already shown (paths without query strings). */
export function withoutShown(matches, shown) {
  return matches.filter((m) => !shown.has(productPath(m.product.url)));
}

export const TEXT = {
  heading: (code, n) =>
    n === 1 ? `Product with manufacturer part number ${code}` : `Products with manufacturer part number ${code}`,
  found: (code, n) => `${n} ${n === 1 ? 'product' : 'products'} found for manufacturer part number ${code}`,
  part: (mpn) => `MPN ${mpn}`,
  stock: (available) => (available ? 'In stock' : 'Out of stock'),
};

const cache = new Map();

class BhlMpnLookup extends HTMLElement {
  #abort = null;
  #observer = null;
  #seq = 0;
  #resolveReady = null;
  #input = null;
  /**
   * Resolves once with the product paths this lookup lists (an empty array when it lists nothing or failed). The
   * supplier code lookup waits for it before opening a product on a page with no results (gateOpen in
   * assets/bhl-supplier-code.js), so the MPN choice is never navigated away from.
   */
  ready = new Promise((resolve) => {
    this.#resolveReady = resolve;
  });

  #listen = null;

  connectedCallback() {
    this.#observer = new MutationObserver(() => this.#settleLater());
    this.#observer.observe(this, { childList: true });
    this.#listen = new AbortController();
    document.addEventListener('bhl:supplier-drawn', () => this.#settleLater(), { signal: this.#listen.signal });
    if (this.dataset.mode === 'predictive') {
      this.#input = this.closest('predictive-search-component')?.querySelector('input[type="search"]') || null;
    }
    this.#run();
  }

  /** predictive: the search input still holds this element's query (always true on the search page) */
  #inputMatches() {
    return !this.#input || normalise(this.#input.value) === normalise(this.dataset.code);
  }

  #answer(paths) {
    this.#resolveReady?.(paths);
    this.#resolveReady = null;
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abort?.abort();
    this.#listen?.abort();
  }

  /** product ids and paths the supplier code panel of THIS search surface lists (it takes precedence, see dedupe) */
  #supplierKeys() {
    return supplierKeysIn(surfaceOf(this), location.origin);
  }

  static get observedAttributes() {
    return ['data-code'];
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.#run();
  }

  get #status() {
    return this.querySelector('.bhl-mpn__status');
  }

  #quietly(fn) {
    this.#observer?.disconnect();
    try {
      fn();
    } finally {
      if (this.isConnected) this.#observer?.observe(this, { childList: true });
    }
  }

  #clear() {
    this.#answer([]);
    this.#quietly(() => {
      this.removeAttribute('data-state');
      this.querySelector('.bhl-mpn__panel')?.remove();
      if (this.#status) this.#status.textContent = '';
    });
  }

  #shown() {
    const predictive = this.dataset.mode === 'predictive';
    const scope = predictive ? this.closest('[data-search-results]') : this.closest('results-list') || document.querySelector('main');
    const shown = new Set();
    if (!scope) return shown;
    for (const a of scope.querySelectorAll('a[href*="/products/"]')) {
      // the supplier code panel is not "the results": what this lookup lists never depends on which answered first
      if (!this.contains(a) && !a.closest('bhl-supplier-code')) shown.add(productPath(a.getAttribute('href') || '', location.origin));
    }
    return shown;
  }

  async #run() {
    this.#abort?.abort();
    const seq = ++this.#seq;
    const lookup = mpnLookupFor(this.dataset.code);
    if (!lookup) {
      this.#clear();
      return;
    }
    if (!cache.has(lookup.query)) {
      const abort = new AbortController();
      this.#abort = abort;
      const url = new URL(this.dataset.endpoint || '/search/suggest.json', location.origin);
      url.searchParams.set('q', lookup.query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(Number(this.dataset.limit) || 10));
      let products;
      try {
        products = await fetchMpn(fetch, url, abort.signal);
      } catch (_) {
        return; // aborted: a newer run answers
      }
      if (seq !== this.#seq) return;
      if (products === 'failed') {
        // unknown, not "no match": the supplier lookup must not navigate on it (gate round 2, condition 1)
        this.#answer('failed');
        this.#clear();
        return;
      }
      cache.set(lookup.query, pickMpn(products, lookup.norm));
    }
    if (seq === this.#seq) this.#settleLater();
  }

  #settleLater() {
    const seq = this.#seq;
    setTimeout(() => {
      if (seq === this.#seq && this.isConnected) this.#settle();
    }, 0);
  }

  #settle() {
    const lookup = mpnLookupFor(this.dataset.code);
    const answer = lookup && cache.get(lookup.query);
    if (!answer) return;
    if (!this.#inputMatches()) return; // the shopper has typed on; the next render brings the element for the new text
    const items = withoutShown(answer, this.#shown());
    // what this lookup found (before the cross-panel rule): the supplier lookup's open decision uses it
    this.#answer(items.map((m) => productPath(m.product.url, location.origin)));
    const visible = dedupe(items, this.#supplierKeys());
    if (!visible.length) {
      this.#clear();
      return;
    }
    this.#draw(lookup, visible);
  }

  #draw(lookup, items) {
    const predictive = this.dataset.mode === 'predictive';
    const panel = document.createElement('section');
    panel.className = 'bhl-mpn__panel';
    const heading = document.createElement(predictive ? 'h4' : 'h2');
    heading.className = predictive ? 'predictive-search-results__title' : 'bhl-mpn__heading h5';
    heading.textContent = TEXT.heading(lookup.code, items.length);
    panel.append(heading);
    const list = document.createElement('ul');
    list.className = predictive ? 'bhl-mpn__list predictive-search-results__list list-unstyled' : 'bhl-mpn__list';
    for (const { product, mpn } of items) {
      const item = document.createElement('li');
      item.className = 'bhl-mpn__item';
      if (product.id != null) item.dataset.productId = String(product.id);
      if (predictive) item.setAttribute('ref', 'resultsItems[]');
      const link = document.createElement('a');
      link.className = 'bhl-mpn__link';
      link.href = productPath(product.url, location.origin);
      const img = document.createElement('img');
      img.className = 'bhl-mpn__img';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 64;
      img.height = 64;
      const src = product.featured_image?.url || product.image;
      if (src) {
        try {
          const u = new URL(src, location.origin);
          u.searchParams.set('width', '128');
          img.src = u.toString();
        } catch (_) {
          img.src = src;
        }
      }
      const text = document.createElement('span');
      text.className = 'bhl-mpn__text';
      const title = document.createElement('span');
      title.className = 'bhl-mpn__title';
      title.textContent = product.title || '';
      const part = document.createElement('span');
      part.className = 'bhl-mpn__part';
      part.textContent = TEXT.part(mpn);
      text.append(title, part);
      if (typeof product.available === 'boolean') {
        const stock = document.createElement('span');
        stock.className = 'bhl-mpn__stock bhl-mpn__stock--' + (product.available ? 'in' : 'out');
        stock.textContent = TEXT.stock(product.available);
        text.append(stock);
      }
      link.append(img, text);
      item.append(link);
      list.append(item);
    }
    panel.append(list);
    this.#quietly(() => {
      this.querySelector('.bhl-mpn__panel')?.remove();
      if (this.#status) this.#status.textContent = TEXT.found(lookup.code, items.length);
      this.append(panel);
      this.setAttribute('data-state', 'found');
    });
    if (predictive) {
      const results = this.closest('[data-search-results]');
      // the dropdown now lists more than the theme's single result, so Enter opens the search page instead
      results?.querySelectorAll('[data-single-result-url]').forEach((el) => {
        if (!this.contains(el)) el.removeAttribute('data-single-result-url');
      });
      results?.querySelectorAll('.predictive-search-results__no-results, .bhl-predictive-help').forEach((el) => {
        if (el instanceof HTMLElement) {
          el.hidden = true;
          el.style.display = 'none';
        }
      });
    } else if (this.dataset.mode === 'page-zero') {
      const zero = this.parentElement?.querySelector(':scope > .bhl-zero .bhl-zero__heading');
      if (zero instanceof HTMLElement) zero.hidden = true;
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('bhl-mpn-lookup')) {
  customElements.define('bhl-mpn-lookup', BhlMpnLookup);
}
